import React from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Users, CheckCircle2, AlertCircle, Edit, Bell } from 'lucide-react';
import type { ApprovalStage } from '@/types/approvalWorkflow';

interface FlowNodeProps {
  stage: ApprovalStage;
  isStart?: boolean;
  isEnd?: boolean;
  onEdit?: () => void;
}

export const FlowNode: React.FC<FlowNodeProps> = ({
  stage,
  isStart = false,
  isEnd = false,
  onEdit,
}) => {
  const approverCount = stage.approvers.length;
  const ruleCount = stage.rules?.length || 0;
  
  // Determine if stage is fully configured
  const isConfigured = approverCount > 0 && ruleCount > 0;

  return (
    <Card
      className="relative w-64 p-6 hover:shadow-xl hover:-translate-y-1 transition-all duration-200 border-2 hover:border-primary/50 group"
    >
      {/* Stage Number Badge with Gradient and Status */}
      <div className="absolute -top-3 -left-3 w-12 h-12 rounded-full bg-gradient-to-br from-primary to-primary/80 text-primary-foreground flex items-center justify-center font-bold text-lg shadow-lg">
        {stage.stageNumber}
        {/* Status Indicator */}
        {isConfigured ? (
          <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-green-500 border-2 border-background flex items-center justify-center">
            <CheckCircle2 className="h-3 w-3 text-white" />
          </div>
        ) : (
          <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-amber-500 border-2 border-background flex items-center justify-center">
            <span className="text-white text-xs font-bold">!</span>
          </div>
        )}
      </div>

      {/* Start/End Badge */}
      {(isStart || isEnd) && (
        <div className="absolute -top-2 -right-2">
          <Badge variant={isStart ? 'default' : 'secondary'} className="text-xs">
            {isStart ? 'START' : 'END'}
          </Badge>
        </div>
      )}

      {/* Edit Button */}
      {onEdit && (
        <Button
          variant="ghost"
          size="icon"
          className="absolute top-2 right-2 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
          onClick={(e) => {
            e.stopPropagation();
            onEdit();
          }}
        >
          <Edit className="h-3 w-3" />
        </Button>
      )}

      {/* Stage Content */}
      <div className="mt-2 space-y-3">
        {/* Stage Name with Gradient Header */}
        <div className="relative -mx-6 -mt-6 mb-4 px-6 py-4 bg-gradient-to-r from-primary/5 via-primary/10 to-primary/5 border-b">
          <h4 className="font-semibold text-sm line-clamp-1">{stage.name}</h4>
          {stage.description && (
            <p className="text-xs text-muted-foreground line-clamp-2 mt-1">
              {stage.description}
            </p>
          )}
        </div>

        {/* Metrics Grid */}
        <div className="space-y-2">
          {/* Approvers - Blue */}
          <div className="flex items-center justify-between p-2 rounded-md bg-blue-50 dark:bg-blue-950/20 border border-blue-100 dark:border-blue-900/50">
            <div className="flex items-center gap-2">
              <div className="p-1 rounded bg-blue-500/10">
                <Users className="h-3 w-3 text-blue-600 dark:text-blue-400" />
              </div>
              <span className="text-xs font-medium text-blue-900 dark:text-blue-100">
                Approvers
              </span>
            </div>
            <span className="text-sm font-bold text-blue-700 dark:text-blue-300">
              {approverCount}
            </span>
          </div>

          {/* Rules - Amber */}
          <div className="flex items-center justify-between p-2 rounded-md bg-amber-50 dark:bg-amber-950/20 border border-amber-100 dark:border-amber-900/50">
            <div className="flex items-center gap-2">
              <div className="p-1 rounded bg-amber-500/10">
                <AlertCircle className="h-3 w-3 text-amber-600 dark:text-amber-400" />
              </div>
              <span className="text-xs font-medium text-amber-900 dark:text-amber-100">
                Rules
              </span>
            </div>
            <span className="text-sm font-bold text-amber-700 dark:text-amber-300">
              {ruleCount}
            </span>
          </div>

          {/* Notifications - Green */}
          <div className="flex items-center justify-between p-2 rounded-md bg-green-50 dark:bg-green-950/20 border border-green-100 dark:border-green-900/50">
            <div className="flex items-center gap-2">
              <div className="p-1 rounded bg-green-500/10">
                <Bell className="h-3 w-3 text-green-600 dark:text-green-400" />
              </div>
              <span className="text-xs font-medium text-green-900 dark:text-green-100">
                Notifications
              </span>
            </div>
            <span className="text-sm font-bold text-green-700 dark:text-green-300">
              {stage.notificationSettings?.enabled ? '✓' : '✗'}
            </span>
          </div>
        </div>
      </div>
    </Card>
  );
};
